// Offline-first queue for field log submissions.
//
// Site basements have no signal and free hosting sleeps, so a submit must never
// make the user wait on the network: entries are written to IndexedDB
// immediately and flushed in the background whenever we're online. Each entry
// carries a client_ref so a retry after a half-completed request can't create
// a duplicate record server-side.
import api from '@/lib/api';

const DB_NAME = 'is-field-log';
const STORE = 'queue';
const CACHE = 'cache';

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'client_ref' });
      if (!db.objectStoreNames.contains(CACHE)) db.createObjectStore(CACHE, { keyPath: 'key' });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function tx(store, mode, fn) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const t = db.transaction(store, mode);
    const s = t.objectStore(store);
    let out;
    try { out = fn(s); } catch (e) { reject(e); return; }
    t.oncomplete = () => resolve(out?.result !== undefined ? out.result : out);
    t.onerror = () => reject(t.error);
  });
}

const newRef = () =>
  `${Date.now()}-${(crypto.randomUUID?.() || Math.random().toString(36).slice(2))}`;

// ── queue ────────────────────────────────────────────────
export async function enqueue(entry) {
  const item = {
    client_ref: newRef(),
    queued_at: new Date().toISOString(),
    attempts: 0,
    status: 'pending',
    ...entry,
  };
  await tx(STORE, 'readwrite', (s) => s.put(item));
  notify();
  flush();                 // fire and forget — never blocks the caller
  return item;
}

export async function listQueue() {
  const items = await tx(STORE, 'readonly', (s) => s.getAll());
  return (items || []).sort((a, b) => (a.queued_at < b.queued_at ? 1 : -1));
}

export async function pendingCount() {
  return (await listQueue()).filter(i => i.status !== 'sent').length;
}

export async function removeEntry(client_ref) {
  await tx(STORE, 'readwrite', (s) => s.delete(client_ref));
  notify();
}

let flushing = false;

export async function flush() {
  if (flushing || !navigator.onLine) return;
  flushing = true;
  try {
    const items = (await listQueue()).filter(i => i.status !== 'sent').reverse(); // oldest first
    for (const item of items) {
      try {
        const res = await api.post('/field-log', {
          category: item.category,
          project_id: item.project_id,
          fields: item.fields,
          draft: item.draft,
          client_ref: item.client_ref,
        });
        await tx(STORE, 'readwrite', (s) => s.put({
          ...item, status: 'sent', sent_at: new Date().toISOString(),
          server_message: res.data?.message || 'Saved',
        }));
        notify();
      } catch (e) {
        const status = e?.response?.status;
        // 4xx (bad data / no permission) will never succeed — stop retrying.
        const permanent = status && status >= 400 && status < 500 && status !== 408 && status !== 429;
        await tx(STORE, 'readwrite', (s) => s.put({
          ...item,
          attempts: (item.attempts || 0) + 1,
          status: permanent ? 'failed' : 'pending',
          error: e?.response?.data?.detail || e.message || 'Send failed',
        }));
        notify();
        if (!permanent) break; // offline or server down — try again later
      }
    }
  } finally {
    flushing = false;
  }
}

export async function retryEntry(client_ref) {
  const items = await listQueue();
  const item = items.find(i => i.client_ref === client_ref);
  if (!item) return;
  await tx(STORE, 'readwrite', (s) => s.put({ ...item, status: 'pending', error: '' }));
  notify();
  flush();
}

export async function clearSent() {
  const items = await listQueue();
  for (const i of items.filter(x => x.status === 'sent')) {
    await tx(STORE, 'readwrite', (s) => s.delete(i.client_ref));
  }
  notify();
}

// ── cached bootstrap (projects/vendors/etc. for offline use) ──
export async function saveCache(key, value) {
  await tx(CACHE, 'readwrite', (s) => s.put({ key, value, at: new Date().toISOString() }));
}

export async function readCache(key) {
  const row = await tx(CACHE, 'readonly', (s) => s.get(key));
  return row?.value ?? null;
}

// ── change notification ──────────────────────────────────
const listeners = new Set();
export function onQueueChange(fn) { listeners.add(fn); return () => listeners.delete(fn); }
function notify() { listeners.forEach(fn => { try { fn(); } catch { /* ignore */ } }); }

if (typeof window !== 'undefined') {
  window.addEventListener('online', flush);
}
